import { 
  DISTANCE_LABELS_SOURCE,
  DISTANCE_LABELS_LAYER
} from './constants';
import { 
  calculateMidpoint, 
  formatDistance, 
  manhattanDistance,
  safeSetSourceData
} from './utils';



// Generate distance label data
export const generateDistanceLabels = (path, pathType, locations) => {
  if (!path || path.length < 2) return [];
  
  const distanceFeatures = [];
  
  for (let i = 0; i < path.length - 1; i++) {
    const currentId = path[i];
    const nextId = path[i + 1];
    
    const currentLocation = locations.find(loc => loc.id === currentId);
    const nextLocation = locations.find(loc => loc.id === nextId);
    
    if (!currentLocation || !nextLocation) continue;
    
    // Validate coordinates
    if (typeof currentLocation.latitude !== 'number' || 
        typeof currentLocation.longitude !== 'number' ||
        typeof nextLocation.latitude !== 'number' || 
        typeof nextLocation.longitude !== 'number' ||
        isNaN(currentLocation.latitude) || isNaN(currentLocation.longitude) ||
        isNaN(nextLocation.latitude) || isNaN(nextLocation.longitude)) {
      continue;
    }
    
    // Calculate distance between two points
    const distance = manhattanDistance(
      currentLocation.latitude,
      currentLocation.longitude,
      nextLocation.latitude,
      nextLocation.longitude
    );
    
    // Calculate midpoint between two points for label position
    const midpoint = calculateMidpoint(
      [currentLocation.longitude, currentLocation.latitude],
      [nextLocation.longitude, nextLocation.latitude]
    );
    
    // Create label feature
    distanceFeatures.push({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: midpoint
      },
      properties: {
        distance: formatDistance(distance),
        pathType: pathType
      }
    });
  }
  
  return distanceFeatures;
};

// Generate distance labels from GeoJSON FeatureCollection (for TP routes)
export const generateDistanceLabelsFromGeoJSON = (geoJSON, pathType) => {
  if (!geoJSON || !geoJSON.features || geoJSON.features.length === 0) {
    return [];
  }
  
  const distanceFeatures = [];
  let totalMapDistance = 0; // Track total distance from map calculation
  
  geoJSON.features.forEach((feature, featureIndex) => {
    if (feature.geometry.type === 'LineString' && feature.geometry.coordinates.length >= 2) {
      const coordinates = feature.geometry.coordinates;
      let featureMapDistance = 0;
      
      // Generate labels for each segment in the route
      for (let i = 0; i < coordinates.length - 1; i++) {
        const startCoord = coordinates[i]; // [lng, lat]
        const endCoord = coordinates[i + 1]; // [lng, lat]
        
        // Calculate distance between two points (convert to lat, lng for calculation)
        const distance = manhattanDistance(
          startCoord[1], startCoord[0], // lat, lng
          endCoord[1], endCoord[0]     // lat, lng
        );
        
        featureMapDistance += distance;
        totalMapDistance += distance;
        
        // Calculate midpoint for label position
        const midpoint = calculateMidpoint(startCoord, endCoord);
        
        // Create label feature
        distanceFeatures.push({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: midpoint
          },
          properties: {
            distance: formatDistance(distance),
            pathType: pathType,
            supplyIndex: feature.properties.supplyIndex || 0
          }
        });
      }
    }
  });
  
  return distanceFeatures;
};

// Update distance labels
export const updateDistanceLabels = ({
  map,
  mapReady,
  sourcesReady,
  shouldShowDistanceLabels,
  shouldShowAutoPath,
  shouldShowCustomPath,
  autoPath,
  customPath,
  locations
}) => {
  // Early exit conditions
  if (!shouldShowDistanceLabels || !mapReady || !sourcesReady || !map) {
    return;
  }

  try {
    // Check if distance labels layer exists
    const layerExists = map.getLayer(DISTANCE_LABELS_LAYER);
    if (!layerExists) {
      console.error(`Distance labels layer not found: ${DISTANCE_LABELS_LAYER}`);
      return;
    }

    // Check if distance labels source exists
    const sourceExists = map.getSource(DISTANCE_LABELS_SOURCE);
    if (!sourceExists) {
      console.error(`Distance labels source not found: ${DISTANCE_LABELS_SOURCE}`);
      return;
    }

    // Generate distance label features for auto path
    let autoDistanceFeatures = [];
    if (shouldShowAutoPath && autoPath) {
      if (autoPath.type === 'FeatureCollection' && autoPath.features) {
        // Handle TP routes GeoJSON format
        autoDistanceFeatures = generateDistanceLabelsFromGeoJSON(autoPath, 'auto');
      } else if (Array.isArray(autoPath) && autoPath.length > 1) {
        // Handle traditional location ID array format
        autoDistanceFeatures = generateDistanceLabels(autoPath, 'auto', locations);
      }
    }
    
    // Generate distance label features for custom path
    let customDistanceFeatures = [];
    if (shouldShowCustomPath && customPath) {
      if (customPath.type === 'FeatureCollection' && customPath.features) {
        // Handle TP custom routes GeoJSON format
        customDistanceFeatures = generateDistanceLabelsFromGeoJSON(customPath, 'custom');
      } else if (Array.isArray(customPath) && customPath.length > 1) {
        // Handle traditional location ID array format
        customDistanceFeatures = generateDistanceLabels(customPath, 'custom', locations);
      }
    }
    
    // Combine all distance labels
    const allDistanceFeatures = [...autoDistanceFeatures, ...customDistanceFeatures];
    
    // Create GeoJSON data structure
    const data = {
      type: 'FeatureCollection',
      features: allDistanceFeatures
    };
    
    // Update distance label data source
    const success = safeSetSourceData(map, mapReady, sourcesReady, DISTANCE_LABELS_SOURCE, data);
    
    if (!success) {
      console.error('Failed to update distance labels source');
    }
    
  } catch (err) {
    console.error("Error updating distance labels:", err);
  }
};

// Set distance label layer visibility - using VRP scaling mechanism
export const setDistanceLabelsVisibility = (map, mapReady, shouldShowDistanceLabels) => {
  if (!map || !mapReady) return;
  
  try {
    // Check current zoom level before setting visibility
    const currentZoom = map.getZoom();
    const shouldAutoHide = currentZoom <= 8; // City level threshold
    
    let finalVisibility;
    if (shouldAutoHide) {
      // At city level, force hide regardless of shouldShowDistanceLabels
      finalVisibility = 'none';
    } else {
      // At non-city level, respect shouldShowDistanceLabels parameter
      finalVisibility = shouldShowDistanceLabels ? 'visible' : 'none';
    }
    
    map.setLayoutProperty(DISTANCE_LABELS_LAYER, 'visibility', finalVisibility);
  } catch (err) {
    console.error("Error setting distance label visibility:", err);
  }
};

 